import React, { useState, useEffect, useRef } from "react";
import { api } from "../../api";
import { Icon, useToast } from "../../components/ui";

const SHEETS = [
  ["SUBMISSION", "Submission"],
  ["END_PROCESS", "End Process"],
  ["MODIFICATION", "Modification"],
  ["MORE_INFO", "More Info"],
];

/* Dedicated import view: one upload card per process. After each import it shows today's imported count
   for that process. Available to every admin. */
export function ImportCenter({ user }) {
  const toast = useToast();
  const [status, setStatus] = useState({});     // sheet -> count imported today
  const [st, setSt] = useState({});             // sheet -> { busy, pct, phase, name, msg, err, conflicts, file }
  const fileRefs = useRef({});

  const loadStatus = () => api.importStatus().then((s) => setStatus(s || {})).catch(() => {});
  useEffect(() => { loadStatus(); }, []);

  const upd = (sheet, patch) => setSt((s) => ({ ...s, [sheet]: { ...(s[sheet] || {}), ...patch } }));

  const run = async (sheet, file, forceUids) => {
    upd(sheet, { busy: true, pct: 0, phase: "uploading", name: file.name, err: "", msg: "", conflicts: null });
    try {
      const res = await api.importPatientRecordsProgress(file, sheet, (pct, phase) => upd(sheet, { pct, phase }), forceUids);
      if (!forceUids && res?.conflicts?.length) {
        upd(sheet, { conflicts: res.conflicts, file, msg: res.message || "" });
      } else {
        upd(sheet, { msg: res?.message || "Import complete", conflicts: null, file: null });
        toast(res?.message || "Import complete", "ok");
      }
      loadStatus();
    } catch (ex) {
      upd(sheet, { err: ex.message || "Import failed" });
    } finally {
      upd(sheet, { busy: false, pct: 0, phase: "" });
    }
  };

  const onPick = (sheet, e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) run(sheet, f, null); };

  return (
    <div>
      <p className="muted" style={{ marginTop: 0, marginBottom: 16 }}>Upload the daily sheet for each process. After an import, this shows how many records came in for that process today.</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
        {SHEETS.map(([sheet, label]) => {
          const s = st[sheet] || {};
          const today = status[sheet];
          const doneToday = typeof today === "number" && today > 0;
          return (
            <div className="card" key={sheet} style={{ padding: 18, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <h3 style={{ margin: 0, fontSize: 15 }}>{label}</h3>
                {doneToday
                  ? <span style={{ background: "#ecfdf5", color: "#065f46", border: "1px solid #a7f3d0", borderRadius: 999, padding: "3px 10px", fontSize: 11.5, fontWeight: 800 }}>✓ Imported today</span>
                  : <span style={{ background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0", borderRadius: 999, padding: "3px 10px", fontSize: 11.5, fontWeight: 700 }}>Not yet today</span>}
              </div>

              <input ref={(el) => { fileRefs.current[sheet] = el; }} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={(e) => onPick(sheet, e)} />
              <button className="btn btn-primary" disabled={s.busy} onClick={() => fileRefs.current[sheet]?.click()}>
                {s.busy ? <span className="spin" /> : <Icon.plus width="15" height="15" />} {s.busy ? "Importing…" : "Upload sheet"}
              </button>

              {s.busy && (
                <div>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>{s.phase === "processing" ? "Processing on the server…" : `Uploading ${s.name || ""} — ${s.pct || 0}%`}</div>
                  <div style={{ height: 6, background: "#eef2f7", borderRadius: 999, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: (s.phase === "processing" ? 100 : (s.pct || 0)) + "%", background: "#4f46e5", transition: "width .2s" }} />
                  </div>
                </div>
              )}

              {s.err && <div className="err-text" style={{ fontSize: 12.5 }}>{s.err}</div>}

              {!s.busy && s.conflicts && s.conflicts.length > 0 && (
                <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 10px", fontSize: 12.5, color: "#92400e" }}>
                  {s.conflicts.length} row(s) match records already worked. {" "}
                  <button className="link-btn" onClick={() => run(sheet, s.file, s.conflicts.map((c) => c.uniqueId))}>Import them again</button>
                </div>
              )}

              {!s.busy && s.msg && !s.conflicts && <div style={{ fontSize: 12.5, color: "#065f46", fontWeight: 600 }}>{s.msg}</div>}

              <div style={{ marginTop: "auto", fontSize: 13, color: "#334155" }}>
                Today's import: <b style={{ color: "#4f46e5" }}>{typeof today === "number" ? today : 0}</b> record{today === 1 ? "" : "s"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}